import React, { useEffect, useMemo, useState } from 'react';
import teamService from '../../services/teamService';
import { calculateTeamMetrics } from '../../shared/metrics/teamMetrics';
import {
  createSingleTeamMetricCharts,
} from '../../shared/charts/metricChartOptions';
import ChartModal, { useChartModal } from '../../shared/components/ChartModal';
import MetricsSummaryGrid from '../../shared/components/MetricsSummaryGrid';
import MetricsChartsSection from '../../shared/components/MetricsChartsSection';
import TeamDetailView from '../../shared/components/TeamDetailView';
import TeamOverviewCard from './components/TeamOverviewCard';
import './TeamDashboard.css';

const TeamDashboard = ({ selectedTeam, theme, csvData }) => {
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedTeamForDetail, setSelectedTeamForDetail] = useState(null);
  const [detailViewType, setDetailViewType] = useState('metrics');
  const {
    chartModalState,
    openChartModal,
    closeChartModal,
    handleChartModalBackdropClick,
  } = useChartModal();

  const loadTeamData = async () => {
    try {
      setLoading(true);
      setError(null);
      const team = await teamService.getTeam(selectedTeam);
      setTeamData(team);
    } catch (loadError) {
      console.error('Ошибка загрузки данных команды:', loadError);
      setError('Ошибка загрузки данных команды');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTeam) {
      loadTeamData();
    }
  }, [selectedTeam]);

  useEffect(() => {
    if (selectedTeam) {
      const timer = setTimeout(loadTeamData, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedTeam]);

  useEffect(() => {
    if (teamData) {
      const timer = setTimeout(() => setTeamData({ ...teamData }), 50);
      return () => clearTimeout(timer);
    }
  }, [theme]);

  const getTeamMetrics = (team) => calculateTeamMetrics(team, csvData, selectedTeam);

  const charts = useMemo(() => {
    if (!teamData) {
      return [];
    }

    const metrics = getTeamMetrics(teamData);
    return createSingleTeamMetricCharts(teamData, metrics, selectedTeam, theme === 'dark');
  }, [teamData, csvData, selectedTeam, theme]);

  if (!selectedTeam) {
    return (
      <div className="team-dashboard">
        <div className="no-team-selected">
          <h3>Выберите команду для просмотра дашборда</h3>
          <p>Пожалуйста, выберите команду в верхней части экрана для отображения аналитики</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="team-dashboard">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Загрузка данных команды...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-dashboard">
        <div className="error-state">
          <p>❌ {error}</p>
          <button onClick={loadTeamData} className="retry-btn" type="button">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!teamData) {
    return (
      <div className="team-dashboard">
        <div className="no-data-state">
          <h3>Данные команды не найдены</h3>
          <p>Не удалось загрузить данные для команды &quot;{selectedTeam}&quot;</p>
        </div>
      </div>
    );
  }

  const metrics = getTeamMetrics(teamData);

  return (
    <div className={`team-dashboard ${theme === 'dark' ? 'dark-theme' : ''}`}>
      <div className="dashboard-header">
        <h2>Дашборд команды: {selectedTeam}</h2>
      </div>

      <TeamOverviewCard
        team={teamData}
        metrics={metrics}
        onShowDetail={(team) => {
          setSelectedTeamForDetail(team);
          setDetailViewType('metrics');
          setShowDetailView(true);
        }}
      />

      <MetricsSummaryGrid metrics={metrics} teamColor={teamData.color} />

      <MetricsChartsSection
        charts={charts}
        chartKeyPrefix={`${selectedTeam}-${teamData.color}`}
        onChartClick={openChartModal}
      />

      <ChartModal
        chartModalState={chartModalState}
        onClose={closeChartModal}
        onBackdropClick={handleChartModalBackdropClick}
      />

      {showDetailView && selectedTeamForDetail && (
        <TeamDetailView
          team={selectedTeamForDetail}
          theme={theme}
          detailViewType={detailViewType}
          onDetailViewTypeChange={setDetailViewType}
          onClose={() => {
            setShowDetailView(false);
            setSelectedTeamForDetail(null);
          }}
          getTeamMetrics={getTeamMetrics}
        />
      )}
    </div>
  );
};

export default TeamDashboard;
